import { clock } from '@/services/clock';
import { parseFromToISO } from '@/shared/lib/date/parseFromToISO';

import { DateRangeType, type ExportSettingsType } from '../types';

const RANGE_SELECTION_REGEX = /^(\d+)([dmy])$/;
const TO_DATE_RANGE_REGEX = /^([1-9]\d*)?(mtd|ytd)$/;

export type ParsedSelectedRange = {
  dateRangeType: DateRangeType;
  periodNumber: string;
  period: ExportSettingsType['period'];
  from: Date | null;
  to: Date | null;
};

const DEFAULT_SELECTED_RANGE: ParsedSelectedRange = {
  dateRangeType: DateRangeType.ALL,
  periodNumber: '2',
  period: 'm',
  from: null,
  to: null
};

const toValidDate = (value: string): Date | null => {
  const from = parseFromToISO(value);
  const parsedDate = new Date(from);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const getRelativeRangeValue = (periodNumber: string, period: ExportSettingsType['period']) =>
  `${periodNumber}${period}`;

export const parseSelectedRange = (
  selectedRange: string | null | undefined
): ParsedSelectedRange => {
  if (!selectedRange || selectedRange === 'all') {
    return DEFAULT_SELECTED_RANGE;
  }

  if (TO_DATE_RANGE_REGEX.test(selectedRange)) {
    const from = toValidDate(selectedRange);
    if (!from) return DEFAULT_SELECTED_RANGE;
    return {
      ...DEFAULT_SELECTED_RANGE,
      dateRangeType: DateRangeType.DATES,
      from,
      to: clock.date()
    };
  }

  const match = RANGE_SELECTION_REGEX.exec(selectedRange);
  if (!match) {
    return DEFAULT_SELECTED_RANGE;
  }

  const parsedPeriod = match[2] as 'd' | 'm' | 'y';
  return {
    ...DEFAULT_SELECTED_RANGE,
    dateRangeType: DateRangeType.RANGE,
    periodNumber: match[1],
    period: parsedPeriod
  };
};
